import { computed, type ComputedRef } from 'vue'
import { useRoute } from '@nuxtjs/composition-api'
import { toNumber } from 'lodash'
import { canEditImpact, useRealUserIsIxAdmin } from '~/functions/impact-locking'
import type {
  GhgSavingPotentialType,
  ImpactGoalType,
  ImpactSolutionType,
  ImpactType,
} from '~/types/types'

export type ImpactEditContextProps = {
  ventureId?: string
  impactGoal?: Partial<ImpactGoalType> | ImpactGoalType
  impact?: ImpactType | Record<string, unknown>
  impactSolution?: ImpactSolutionType | Record<string, unknown>
}

export type ImpactEditContext = {
  ventureId: ComputedRef<string>
  impactGoal: ComputedRef<Partial<ImpactGoalType>>
  impactId: ComputedRef<number>
  impact: ComputedRef<ImpactType | undefined>
  impactSolution: ComputedRef<ImpactSolutionType | undefined>
  ghgSavingPotential: ComputedRef<GhgSavingPotentialType | undefined>
  impactEditable: ComputedRef<boolean>
  accountId: ComputedRef<string>
}

export function useImpactEditContext(
  props: ImpactEditContextProps = {},
): ImpactEditContext {
  const route = useRoute()

  const ventureId = computed(
    () => props.ventureId ?? String(route.params.venture ?? ''),
  )

  const impactGoal = computed<Partial<ImpactGoalType>>(
    () => props.impactGoal ?? {},
  )

  const impactId = computed(() => parseInt(String(route.params.impact), 10))

  const solutionIndex = computed(() => {
    const raw = route.params.solution
    if (raw === undefined || raw === '') return NaN
    return toNumber(raw)
  })

  const impact = computed<ImpactType | undefined>(() => {
    if (props.impact) return props.impact as ImpactType
    return impactGoal.value?.impacts?.find((e) => e.id === impactId.value)
  })

  const impactSolution = computed<ImpactSolutionType | undefined>(() => {
    if (props.impactSolution) return props.impactSolution as ImpactSolutionType
    const solutions = impact.value?.impactSolutions
    if (!solutions || Number.isNaN(solutionIndex.value)) return undefined
    return solutions[solutionIndex.value]
  })

  const ghgSavingPotential = computed(
    () => impactSolution.value?.ghgSavingPotential ?? undefined,
  )

  const realUserIsIxAdmin = useRealUserIsIxAdmin()
  const impactEditable = computed(() =>
    canEditImpact(impact.value, realUserIsIxAdmin.value),
  )

  const accountId = computed(() => ventureId.value)

  return {
    ventureId,
    impactGoal,
    impactId,
    impact,
    impactSolution,
    ghgSavingPotential,
    impactEditable,
    accountId,
  }
}
